import '../trailingSlash.js'
import './index.css'
import {
  parseTestMode,
  escapeHtml,
  applyTestModeBadge as applyTestModeBadgeShared,
  applyBranding as applyBrandingShared
} from '../shared/branding.js'
const { hasActiveEntitlement, waitForEntitlement } = require('./paymentState.js')
const {
  isFlowsPreview,
  flowsPreviewState,
  observeFlowsSize
} = require('../shared/flows-preview.js')
const { ICONS } = require('../shared/icons.js')

const API_URL = 'https://api.payhook.link'

const CHECK_ICON = ICONS.checkCircleSolid

const rootEl = document.getElementById('success-root')
const brandLogoEl = document.getElementById('success-brand-logo')
const brandNameEl = document.getElementById('success-brand-name')
const titleEl = document.getElementById('success-title')
const leadEl = document.getElementById('success-lead')
const featuresEl = document.getElementById('success-features')
const hintEl = document.getElementById('success-hint')
const copyEl = document.getElementById('success-copy')
const testBadgeEl = document.getElementById('success-test-badge')
const footerEl = document.querySelector('.site-footer')

// Success copy starts as the page defaults and is replaced by merchant
// branding; it is only written to the DOM once the payment is confirmed.
const successCopy = {
  title: titleEl.textContent,
  lead: leadEl.textContent,
  hint: null,
  featuresHtml: ''
}

const PENDING_TITLE = 'Waiting for your payment to confirm…'
const PENDING_LEAD = 'Paid with Pix? It confirms a few seconds after you approve it in your bank app. Keep this window open.'
const PENDING_HINT = 'Paid by boleto? It takes 1–3 business days to clear. Pro unlocks automatically once it does.'
const STILL_PENDING_TITLE = 'Your payment is still processing'
const STILL_PENDING_LEAD = 'Pro unlocks automatically as soon as your payment clears, and we\'ll email you when it does. Boleto payments take 1–3 business days.'

const params = new URLSearchParams(window.location.search)
const extensionId = params.get('extensionId')
const paymentHookId = params.get('paymentHookId')
const testModeParam = params.get('testMode')

const testMode = parseTestMode(testModeParam)

// `?preview=flows`: never pulls payments and never messages the extension.
// `previewState` picks the outcome to show: a closing line notifyExtension
// would settle on, or one of the pending states a Pix/boleto payment shows.
const flowsPreview = isFlowsPreview()
const SUCCESS_PREVIEW_COPY = {
  synced: 'All set — you can close this window.',
  returning: 'Returning to your extension…',
  unsynced: 'Open your extension to finish syncing.',
  standalone: 'You can close this window.'
}
const SUCCESS_PREVIEW_STATES = [...Object.keys(SUCCESS_PREVIEW_COPY), 'pending', 'still-pending']
const successPreviewState = flowsPreviewState(window.location.search, SUCCESS_PREVIEW_STATES) || 'synced'

observeFlowsSize(rootEl, { include: [footerEl] })

function applyTestModeBadge () {
  applyTestModeBadgeShared(rootEl, testBadgeEl, testMode, 'success--test-mode')
}

applyTestModeBadge()

function setCopy (message) {
  if (copyEl) copyEl.textContent = message
}

function applyBranding (branding) {
  applyBrandingShared(branding, {
    rootEl,
    accentVarName: '--success-accent',
    accentSoftVarName: '--success-accent-soft',
    hasLogoClass: 'success--has-logo',
    softOpacity: 0.22,
    applyLogo: (b) => {
      brandLogoEl.src = b.logoUrl
      brandLogoEl.alt = b.extensionName || 'Logo'
      brandLogoEl.hidden = false
      brandNameEl.hidden = true
      brandNameEl.textContent = ''
    },
    applyNoLogo: (b) => {
      brandLogoEl.hidden = true
      brandLogoEl.removeAttribute('src')
      if (b.extensionName) {
        brandNameEl.hidden = false
        brandNameEl.textContent = b.extensionName
      } else {
        brandNameEl.hidden = true
        brandNameEl.textContent = ''
      }
    },
    extra: (b) => {
      if (b.successTitle) successCopy.title = b.successTitle
      if (b.successLead) successCopy.lead = b.successLead
      if (b.successHint) successCopy.hint = b.successHint

      // Success page mirrors slowed.app/upgraded — every configured feature
      // shown as unlocked, included or otherwise.
      const features = Array.isArray(b.features) ? b.features : []
      if (features.length > 0) {
        successCopy.featuresHtml = features.map((f) => `
          <div class="success__feature" role="listitem">
            <span class="success__feature-icon">${CHECK_ICON}</span>
            <span>${escapeHtml(f.label)}</span>
          </div>
        `).join('')
      }
    }
  })
}

async function fetchBranding () {
  if (!paymentHookId) return null
  try {
    const headers = {}
    if (testMode) headers['Test-Mode'] = 'True'
    const url = new URL(`${API_URL}/unlock`)
    url.searchParams.set('paymentHookId', paymentHookId)
    url.searchParams.set('purpose', 'branding')
    const response = await fetch(url.href, { headers })
    if (!response.ok) return null
    const data = await response.json()
    return data && data.branding
  } catch {
    return null
  }
}

function notifyExtension () {
  const runtime = globalThis.chrome?.runtime
  if (!extensionId || !runtime?.sendMessage) {
    setCopy('You can close this window.')
    return
  }
  try {
    runtime.sendMessage(extensionId, { command: 'payhook:checkout-complete' }, () => {
      if (runtime.lastError) {
        setCopy('Open your extension to finish syncing.')
        return
      }
      setCopy('All set — you can close this window.')
    })
  } catch {
    setCopy('Open your extension to finish syncing.')
  }
}

// The same live Stripe read the extension uses. Public and unauthenticated
// by hook id, like the branding request above.
async function pullPayments () {
  if (!paymentHookId) throw new Error('Missing payment hook')
  const headers = {}
  if (testMode) headers['Test-Mode'] = 'True'
  const url = new URL(`${API_URL}/v1/payment_hooks/${encodeURIComponent(paymentHookId)}/pull`)
  url.searchParams.set('force', '1')
  const response = await fetch(url.href, { headers, cache: 'no-store' })
  if (!response.ok) throw new Error(`Pull failed (${response.status})`)
  const data = await response.json()
  return data && data.payments
}

// 'active' | 'pending' | 'unknown'. Unknown (no hook, API down) falls back to
// the historical behavior: the extension runs its own authoritative pull
// when notified, so the page must not strand a card buyer on a spinner.
async function checkEntitlement () {
  try {
    return hasActiveEntitlement(await pullPayments()) ? 'active' : 'pending'
  } catch {
    return 'unknown'
  }
}

function setState (state) {
  rootEl.classList.toggle('success--pending', state === 'pending')
}

function renderSuccess ({ notify = true } = {}) {
  setState('success')
  titleEl.textContent = successCopy.title
  leadEl.textContent = successCopy.lead
  if (successCopy.hint) {
    hintEl.textContent = successCopy.hint
    hintEl.hidden = false
  } else {
    hintEl.hidden = true
  }
  if (successCopy.featuresHtml) {
    featuresEl.innerHTML = successCopy.featuresHtml
    featuresEl.hidden = false
  }
  document.title = 'Payment complete — Payhook'
  if (notify) notifyExtension()
}

function renderPending () {
  setState('pending')
  titleEl.textContent = PENDING_TITLE
  leadEl.textContent = PENDING_LEAD
  hintEl.textContent = PENDING_HINT
  hintEl.hidden = false
  featuresEl.hidden = true
  setCopy('Checking your payment…')
  document.title = 'Confirming payment — Payhook'
}

function renderStillPending () {
  titleEl.textContent = STILL_PENDING_TITLE
  leadEl.textContent = STILL_PENDING_LEAD
  hintEl.hidden = true
  setCopy('You can close this window.')
}

async function initializePreview () {
  try {
    applyBranding(await fetchBranding())
  } catch (error) {
    console.warn('Failed to apply success branding', error)
  }
  if (successPreviewState === 'pending') {
    renderPending()
  } else if (successPreviewState === 'still-pending') {
    renderPending()
    renderStillPending()
  } else {
    renderSuccess({ notify: false })
    setCopy(SUCCESS_PREVIEW_COPY[successPreviewState])
  }
  rootEl.hidden = false
}

async function initialize () {
  if (flowsPreview) return initializePreview()

  const [branding, entitlement] = await Promise.all([
    fetchBranding(),
    checkEntitlement()
  ])
  try {
    applyBranding(branding)
  } catch (error) {
    console.warn('Failed to apply success branding', error)
  }

  if (entitlement === 'pending') {
    renderPending()
  } else {
    renderSuccess()
  }

  // Keep the animated success state out of the rendering tree until its
  // accent is configured. Otherwise the animation begins in Payhook blue and
  // changes color partway through when merchant branding arrives.
  rootEl.hidden = false

  if (entitlement !== 'pending') return

  // Pix confirms asynchronously; keep the window (and the extension's
  // close-to-refresh hook) alive until it does, then tell the extension.
  const outcome = await waitForEntitlement({ pull: pullPayments })
  if (outcome === 'active') {
    renderSuccess()
  } else {
    renderStillPending()
  }
}

initialize()
